import { useEffect } from 'react';
import * as chatwoot from 'ahize/chatwoot';
import { useSelfNullable } from '@/hooks/use-self';
import { useTenantNullable } from '@/hooks/use-tenant';
import * as React from 'react';
import { createUseAhize } from 'ahize/react';
import pThrottle, { AnyFunction } from 'p-throttle';
import { useI18nContext } from '@/i18n/i18n-react';
import { useChatwootWidget } from './chatwoot-widget-context';

const BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL;
const WEBSITE_TOKEN = import.meta.env.VITE_CHATWOOT_WEBSITE_TOKEN;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useAhize = createUseAhize(React as any);

// Without throttling chatwoot seems to run into issues, possibly
// race conditions.
const throttle = pThrottle({ limit: 1, interval: 1000 });
const runThrottledFn = throttle((fn: AnyFunction) => fn());

export function ChatwootWidget() {
  const self = useSelfNullable();
  const tenant = useTenantNullable();
  const { LL, locale } = useI18nContext();

  // Note: chatwoot widget is styled in the global CSS file
  const { identify, shutdown, hide, show } =
    useAhize<chatwoot.ChatwootLoadOptions>({
      provider: chatwoot,
      options: {
        websiteToken: WEBSITE_TOKEN ?? '',
        baseUrl: BASE_URL ?? '',
        launcherTitle: LL.common.help(),
        type: 'expanded_bubble',
        position: 'left',
      },
    });
  const { isOpen, setIsOpen } = useChatwootWidget();

  useEffect(() => {
    if (self) {
      runThrottledFn(() =>
        identify({
          id: self.id,
          email: self.email,
          verification: {
            kind: 'hmac',
            hash: self.chatwootIdentifierHash,
          },
        })
      );
    } else {
      runThrottledFn(() => shutdown());
    }
  }, [self, identify, shutdown]);

  useEffect(() => {
    chatwoot.setLocale(locale === 'pt' ? 'pt_BR' : 'en');
  }, [locale]);

  useEffect(() => {
    const offs: (() => void)[] = [];

    offs.push(
      chatwoot.on('startConversation', async () => {
        if (tenant) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (window as any).$chatwoot?.setConversationCustomAttributes({
            tenant_id: tenant.id,
            tenant_name: tenant.name,
          });
        }
      })
    );

    offs.push(
      chatwoot.on('closed', () => {
        setIsOpen(false);
      })
    );

    offs.push(
      chatwoot.on('opened', () => {
        setIsOpen(true);
      })
    );

    return () => offs.forEach((off) => off());
  }, [tenant, setIsOpen]);

  useEffect(() => {
    chatwoot.setBubbleVisibility('hide');
  }, []);

  useEffect(() => {
    if (isOpen) {
      show();
    } else {
      hide();
    }
  }, [isOpen, show, hide]);

  return null;
}
